import { useState } from 'react';
import type { ReactNode } from 'react';

import type { ApiEndpoint, HealthSummary } from '../../api/monitor';
import type { NotificationsController } from '../../hooks/useNotifications';
import { MobileTabs } from './MobileTabs';
import { MoreSheet } from './MoreSheet';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { screenTitle } from './navigation';
import type { ScreenId } from './navigation';
import type { ShellUser } from './UserBadge';

interface AppShellProps {
  screen: ScreenId;
  onNavigate: (screen: ScreenId) => void;
  user: ShellUser;
  /** Saúde geral mostrada no cabeçalho — vem da tela de APIs. */
  summary: HealthSummary | null;
  apis: ApiEndpoint[];
  /** Central de notificações do cabeçalho. */
  notifications: NotificationsController;
  onSignOut?: () => void;
  children: ReactNode;
}

/**
 * Moldura das telas internas: sidebar no desktop, tabs + bottom sheet no
 * celular e o cabeçalho com o status geral das APIs. Toda tela nova entra aqui
 * como `children` — nada de layout precisa ser reescrito.
 */
export function AppShell({
  screen,
  onNavigate,
  user,
  summary,
  apis,
  notifications,
  onSignOut,
  children,
}: AppShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);

  const navigate = (next: ScreenId) => {
    setMoreOpen(false);
    onNavigate(next);
  };

  return (
    <div className="flex h-dvh overflow-hidden">
      <Sidebar current={screen} onNavigate={navigate} user={user} onSignOut={onSignOut} />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          title={screenTitle(screen)}
          summary={summary}
          apis={apis}
          user={user}
          notifications={notifications}
          onNavigate={navigate}
        />

        <main className="mx-auto w-full max-w-[960px] flex-1 overscroll-contain overflow-auto px-[18px] pt-[18px] pb-[90px]">
          {children}
        </main>

        <MobileTabs
          current={screen}
          onNavigate={navigate}
          onOpenMore={() => setMoreOpen(true)}
        />
      </div>

      {moreOpen ? (
        <MoreSheet
          onNavigate={navigate}
          onClose={() => setMoreOpen(false)}
          onSignOut={onSignOut}
        />
      ) : null}
    </div>
  );
}
